import { Position } from '@deck.gl/core';
import { ColorVector, FuelDto, MonitorStatus, parseColor255 } from '@fe/core';
import { IconType } from '../IconType';
import { ObjectClass } from '../ObjectClass';
import { FlightUpdateGeojsonProps, IFlightItem, LngLatArray } from './types';

const DEFAULT_TAG_OFFSET = 10;
const POSITION_HISTORY_MAX_COUNT = 40;

// Speed [knot]
// Distances [NM]
// Angles [rad]
// Position [deg, deg]
export default class FlightItem implements IFlightItem {
    readonly id: number;

    // Group into an object?
    position: LngLatArray = [0, 0];
    speed: number = 0;
    altitude: number | null = null;
    heading: number | null = null;
    feedId: number | null = null;
    positionReportTime: number | null = null;
    fob: FuelDto | null = null;

    lastRealReport?: {
        position: LngLatArray;
        speed: number | null;
        altitude: number | null;
        heading: number | null;
        feedId: number | null;
        positionReportTime: number;
    };

    tagContent: string | null = null;
    acType: string | null = null;
    objectClass = ObjectClass.unknown;
    onGround = false;

    alertState?: {
        marker: boolean;
        tag: boolean;
        color: ColorVector | null;
    } = undefined;

    monitorStatus = MonitorStatus.null;

    /** move to filter info object */
    filterTagColorSameAsPlane: boolean = false;
    filterColor: ColorVector | null = null;
    filterTag = false;
    filterHistory = false;

    tagOffsetX = DEFAULT_TAG_OFFSET;
    tagOffsetY = DEFAULT_TAG_OFFSET;

    iconType: IconType = IconType.circle;
    history: LngLatArray[] = [];

    // TODO: we should need those on the flight record level here, just in deck props
    hasIndividualTag = false;
    hasIndividualVector = false;
    hasIndividualRing = false;
    hasIndividualHistory = false;

    // Diversion
    destinationPosition: Position | null = null;
    oldDestinationPosition: Position | null = null;

    coastFromPosition: LngLatArray | null = null;
    coastFromTime: number | null = null;
    coastToPosition: LngLatArray | null = null;
    coastToTime: number | null = null;

    constructor(id: number) {
        this.id = id;
    }

    // TODO: make flight item independent of workspace number
    update(data: FlightUpdateGeojsonProps, workspaceId: number, coastOffsetTime: number) {
        this.position = data.position;
        this.speed = data.speed;
        this.altitude = data.altitude;
        this.heading = data.heading;
        this.feedId = data.feedId;
        this.positionReportTime = data.positionReportTime;

        if (data.lastRealPosition) {
            this.coastFromPosition = data.coastFromPosition;
            this.coastFromTime = data.coastFromTime;
            this.coastToPosition = data.coastToPosition;
            this.coastToTime = data.coastToTime;

            this.lastRealReport ??= {} as any;
            this.lastRealReport!.position = data.lastRealPosition;
            this.lastRealReport!.speed = data.lastRealSpeed;
            this.lastRealReport!.altitude = data.lastRealAltitude;
            this.lastRealReport!.heading = data.lastRealHeading;
            this.lastRealReport!.feedId = data.lastRealFeedId;
            this.lastRealReport!.positionReportTime = data.lastRealPositionReportTime ?? 0;
        }

        this.iconType = data.iconType;
        this.objectClass = data.objectClass;
        this.acType = data.acType;

        this.alertState = data.alertState
            ? {
                  marker: data.alertState.marker,
                  tag: data.alertState.tag,
                  color: data.alertState.color ? parseColor255(data.alertState.color) : null
              }
            : undefined;

        this.onGround = data.onGround;
        this.destinationPosition = data.diversion?.destinationPosition ?? null;
        this.oldDestinationPosition = data.diversion?.oldDestinationPosition ?? null;
        this.tagContent = data.tags?.[workspaceId] ?? null;
        this.monitorStatus = data.monitorStatus;
        this.fob = data.fob;

        // Individual
        this.hasIndividualVector = data.hasIndividualVector;
        this.hasIndividualTag = data.hasIndividualTag;
        this.hasIndividualRing = data.hasIndividualRing;
        this.hasIndividualHistory = data.hasIndividualHistory;

        // Filter
        this.filterColor = data.filterState[workspaceId].color ? parseColor255(data.filterState[workspaceId].color) : null;
        this.filterTag = data.filterState[workspaceId].tags;
        this.filterTagColorSameAsPlane = data.filterState[workspaceId].tagColorSameAsPlane;
        this.filterHistory = data.filterState[workspaceId].history;

        // History is optional, so only override if present
        if (data.history) {
            this.history = data.history;
        } else {
            this.history.push(data.position);
            if (this.history.length > POSITION_HISTORY_MAX_COUNT) {
                this.history.shift();
            }
        }
    }

    // The position modified each update in endless mode or the position just before toTarget mode has started
    // Changing currentPosition causes the object to jump to the new position
    /** replace this with a property that know if projected point are on or not */
    get currentPosition(): LngLatArray {
        // FIXME: at least currently it's broken in the same way as as before
        return this.position;
    }

    get isAircraft() {
        return this.objectClass === ObjectClass.air;
    }

    get isGroundVehicle() {
        return this.objectClass === ObjectClass.road;
    }

    get isDiverting() {
        return this.destinationPosition != null && this.oldDestinationPosition != null;
    }
}
